import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthForm from './components/AuthForm';
import './App.css';

// Mock data for development
const mockPosts = [
  {
    id: 1,
    type: 'general',
    author: 'Sarah M.',
    neighborhood: 'Maple Street',
    time: '2 hours ago',
    content: 'Has anyone seen a small orange cat around Oak Avenue? He\'s been missing since yesterday morning. His name is Milo and he\'s very friendly.',
    likes: 12,
    comments: [
      { author: 'Mike R.', content: 'I think I saw him near the park this morning!', time: '1 hour ago' },
      { author: 'Lisa K.', content: 'I\'ll keep an eye out. Hope you find him soon! 🐱', time: '45 min ago' }
    ]
  },
  {
    id: 2,
    type: 'marketplace',
    author: 'John D.',
    neighborhood: 'Pine Boulevard',
    time: '4 hours ago',
    content: 'Moving sale this weekend! Lots of furniture and household items.',
    price: '$10-200',
    condition: 'Various conditions',
    likes: 8,
    comments: [
      { author: 'Emma S.', content: 'Do you have any dining chairs?', time: '2 hours ago' }
    ]
  },
  {
    id: 3,
    type: 'general',
    author: 'Maria G.',
    neighborhood: 'Cedar Lane',
    time: '6 hours ago',
    content: 'Thank you to whoever left the beautiful flowers on my doorstep! Made my day! 🌸',
    likes: 24,
    comments: []
  }
];

const mockMarketplaceItems = [
  {
    id: 1,
    title: 'Vintage Dining Table',
    price: 150,
    condition: 'Good',
    author: 'Jennifer L.',
    neighborhood: 'Oak Avenue',
    time: '1 day ago',
    image: null,
    description: 'Beautiful solid wood dining table, seats 6. Minor scratches but very sturdy.'
  },
  {
    id: 2,
    title: 'Kids Bike - Red',
    price: 45,
    condition: 'Like New',
    author: 'David P.',
    neighborhood: 'Maple Street',
    time: '2 days ago',
    image: null,
    description: 'My son outgrew this bike. Perfect for ages 6-8.'
  },
  {
    id: 3,
    title: 'Free - Moving Boxes',
    price: 0,
    condition: 'Good',
    author: 'Rachel M.',
    neighborhood: 'Pine Boulevard',
    time: '3 days ago',
    image: null,
    description: 'About 20 moving boxes, various sizes. Must pick up by weekend.'
  }
];

// Components
function Header() {
  const { user, logout } = useAuth();
  
  return (
    <header className="header">
      <div className="header-content">
        <div>
          <div className="logo">Nextdoor</div>
          <div className="neighborhood-name">{user?.neighborhood || 'Your Neighborhood'}</div>
        </div>
        <button 
          onClick={logout}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            padding: '0.5rem',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '1.2rem'
          }}
        >
          👤
        </button>
      </div>
    </header>
  );
}

function BottomNav() {
  const location = useLocation();
  
  return (
    <nav className="bottom-nav">
      <Link to="/" className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
        <div className="nav-icon">🏠</div>
        <div>Feed</div>
      </Link>
      <Link to="/marketplace" className={`nav-item ${location.pathname === '/marketplace' ? 'active' : ''}`}>
        <div className="nav-icon">🛒</div>
        <div>For Sale</div>
      </Link>
      <Link to="/post" className={`nav-item ${location.pathname === '/post' ? 'active' : ''}`}>
        <div className="nav-icon">➕</div>
        <div>Post</div>
      </Link>
      <Link to="/messages" className={`nav-item ${location.pathname === '/messages' ? 'active' : ''}`}>
        <div className="nav-icon">💬</div>
        <div>Messages</div>
      </Link>
    </nav>
  );
}

function PostCreator({ onPost, postType = 'general' }) {
  const [content, setContent] = useState('');
  const [currentType, setCurrentType] = useState(postType);
  const [price, setPrice] = useState('');
  const [condition, setCondition] = useState('Good');
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!content.trim()) return;
    
    const newPost = {
      id: Date.now(),
      type: currentType,
      author: `${user.firstName} ${user.lastName.charAt(0)}.`,
      neighborhood: user.neighborhood,
      time: 'Just now',
      content,
      price: currentType === 'marketplace' ? price : undefined,
      condition: currentType === 'marketplace' ? condition : undefined,
      likes: 0,
      comments: []
    };
    
    // In production, this would call the API
    // await fetch('/api/posts', { method: 'POST', ... })
    
    onPost(newPost);
    setContent('');
    setPrice('');
  };

  return (
    <div className="post-creator">
      <textarea
        className="post-input"
        placeholder={currentType === 'marketplace' ? "What are you selling?" : "What's happening in your neighborhood?"}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      
      {currentType === 'marketplace' && (
        <div style={{ marginBottom: '0.75rem' }}>
          <input
            type="text"
            placeholder="Price (e.g., $50 or Free)"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{ 
              width: '48%', 
              padding: '0.5rem', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              marginRight: '4%'
            }}
          />
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            style={{ 
              width: '48%', 
              padding: '0.5rem', 
              border: '1px solid #ddd', 
              borderRadius: '4px'
            }}
          >
            <option>New</option>
            <option>Like New</option>
            <option>Good</option>
            <option>Fair</option>
          </select>
        </div>
      )}
      
      <div className="post-actions">
        <div className="post-type-selector">
          <button
            className={`post-type-btn ${currentType === 'general' ? 'active' : ''}`}
            onClick={() => setCurrentType('general')}
          >
            💬 General
          </button>
          <button
            className={`post-type-btn ${currentType === 'marketplace' ? 'active' : ''}`}
            onClick={() => setCurrentType('marketplace')}
          >
            🛒 For Sale
          </button>
        </div>
        <button
          className="post-btn"
          onClick={handleSubmit}
          disabled={!content.trim()}
        >
          Post
        </button>
      </div>
    </div>
  );
}

function FeedPost({ post, onLike, onComment }) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);

  const handleLike = () => {
    setLiked(!liked);
    onLike(post.id);
  };

  const handleComment = () => {
    if (!newComment.trim()) return;
    onComment(post.id, newComment);
    setNewComment('');
  };

  return (
    <div className="feed-post">
      <div className="post-header">
        <div className="post-avatar">
          {post.author.charAt(0)}
        </div>
        <div className="post-author-info">
          <h4>{post.author}</h4>
          <div className="post-meta">{post.neighborhood} • {post.time}</div>
        </div>
        <div className={`post-type-badge ${post.type}`}>
          {post.type === 'marketplace' ? '🛒 For Sale' : '💬 General'}
        </div>
      </div>
      
      <div className="post-content">
        <div className="post-text">{post.content}</div>
        
        {post.type === 'marketplace' && (
          <div className="marketplace-details">
            <div className="price">{post.price}</div>
            <div className="item-condition">Condition: {post.condition}</div>
          </div>
        )}
      </div>
      
      <div className="post-actions-bar">
        <button className={`action-btn ${liked ? 'liked' : ''}`} onClick={handleLike}>
          <span>{liked ? '❤️' : '🤍'}</span>
          <span>{post.likes + (liked ? 1 : 0)}</span>
        </button>
        <button className="action-btn" onClick={() => setShowComments(!showComments)}>
          <span>💬</span>
          <span>{post.comments.length}</span>
        </button>
        {post.type === 'marketplace' && (
          <button className="action-btn">
            <span>📧</span>
            <span>Message</span>
          </button>
        )}
      </div>
      
      {showComments && (
        <div className="comments-section">
          {post.comments.map((comment, index) => (
            <div key={index} className="comment">
              <div className="comment-avatar">{comment.author.charAt(0)}</div>
              <div className="comment-content">
                <div className="comment-author">{comment.author}</div>
                <div className="comment-text">{comment.content}</div>
                <div className="comment-time">{comment.time}</div>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <input
              type="text"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '20px',
                outline: 'none'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleComment()}
            />
            <button
              onClick={handleComment}
              style={{
                background: '#00a956',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer'
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Feed() {
  const [posts, setPosts] = useState(mockPosts);
  const { user } = useAuth();

  const handleNewPost = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const handleLike = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, likes: post.likes + 1 }
        : post
    ));
  };

  const handleComment = (postId, comment) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { 
            ...post, 
            comments: [...post.comments, {
              author: `${user.firstName} ${user.lastName.charAt(0)}.`,
              content: comment,
              time: 'Just now'
            }]
          }
        : post
    ));
  };

  return (
    <div className="main-content">
      <PostCreator onPost={handleNewPost} />
      {posts.map(post => (
        <FeedPost
          key={post.id}
          post={post}
          onLike={handleLike}
          onComment={handleComment}
        />
      ))}
    </div>
  );
}

function Marketplace() {
  const [items, setItems] = useState(mockMarketplaceItems);
  const [filter, setFilter] = useState('all');

  const filters = [
    { key: 'all', label: 'All Items' },
    { key: 'free', label: 'Free' },
    { key: 'under50', label: 'Under $50' },
    { key: 'furniture', label: 'Furniture' }
  ];

  const filteredItems = items.filter(item => {
    if (filter === 'free') return item.price === 0;
    if (filter === 'under50') return item.price < 50 && item.price > 0;
    return true;
  });

  const handleNewPost = (newPost) => {
    if (newPost.type === 'marketplace') {
      const marketplaceItem = {
        id: Date.now(),
        title: newPost.content.split('.')[0] || newPost.content,
        price: parseInt(newPost.price?.replace(/[^0-9]/g, '')) || 0,
        condition: newPost.condition,
        author: newPost.author,
        neighborhood: newPost.neighborhood,
        time: newPost.time,
        description: newPost.content
      };
      setItems([marketplaceItem, ...items]);
    }
  };

  return (
    <div className="main-content">
      <PostCreator onPost={handleNewPost} postType="marketplace" />
      
      <div className="marketplace-filters">
        {filters.map(f => (
          <button
            key={f.key}
            className={`filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🛒</div>
          <h3>No items found</h3>
          <p>Try adjusting your filters or check back later</p>
        </div>
      ) : (
        filteredItems.map(item => (
          <div key={item.id} className="feed-post">
            <div className="post-header">
              <div className="post-avatar">{item.author.charAt(0)}</div>
              <div className="post-author-info">
                <h4>{item.author}</h4>
                <div className="post-meta">{item.neighborhood} • {item.time}</div>
              </div>
              <div className="post-type-badge marketplace">🛒 For Sale</div>
            </div>
            
            <div className="post-content">
              <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>{item.title}</h3>
              <div className="post-text">{item.description}</div>
              
              <div className="marketplace-details">
                <div className="price">
                  {item.price === 0 ? 'FREE' : `$${item.price}`}
                </div>
                <div className="item-condition">Condition: {item.condition}</div>
              </div>
            </div>
            
            <div className="post-actions-bar">
              <button className="action-btn">
                <span>❤️</span>
                <span>Save</span>
              </button>
              <button className="action-btn">
                <span>📧</span>
                <span>Message</span>
              </button>
              <button className="action-btn">
                <span>📍</span>
                <span>Directions</span>
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function CreatePost() {
  const [posts, setPosts] = useState([]);

  const handleNewPost = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  return (
    <div className="main-content">
      <h2 style={{ marginBottom: '1rem', textAlign: 'center' }}>Create a Post</h2>
      <PostCreator onPost={handleNewPost} />
      
      {posts.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Your Recent Posts</h3>
          {posts.map(post => (
            <FeedPost key={post.id} post={post} onLike={() => {}} onComment={() => {}} />
          ))}
        </div>
      )}
    </div>
  );
}

function Messages() {
  return (
    <div className="main-content">
      <div className="empty-state">
        <div className="empty-state-icon">💬</div>
        <h3>No messages yet</h3>
        <p>Start a conversation with your neighbors</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <AuthForm />;
  }

  return (
    <div className="app">
      <Header />
      <Routes>
        <Route path="/" element={<Feed />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/post" element={<CreatePost />} />
        <Route path="/messages" element={<Messages />} />
      </Routes>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;